import { unwrap } from "@sagot/api-client";
import { ATTACHMENT_RETENTION_DAY_OPTIONS, RETENTION_DAY_OPTIONS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Field, Icon, Select, toast } from "@sagot/ui";
import { useState } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceId } from "../../../../session/workspace-context";
import { wsKeys, type PrivacyOut } from "../../shared";
import { useInvalidate, usePrivacySettings } from "../use-settings";
import { DataExportCard } from "./data-export-card";
import { DangerZone } from "./delete-workspace";
import { EgressIpsCard } from "./egress-ips-card";
import { IdentityBindingCard } from "./identity-binding-card";
import { CardActions, CardSkeleton, PanelError, useCanManage } from "./panel-kit";
import { RedactionCard } from "./redaction-card";
import { kindsOf } from "./redaction-model";
import { RetentionPresetsCard } from "./retention-presets-card";
import { SecurityActivityCard } from "./security-activity-card";
import { WebhookReferenceCard } from "./webhook-reference-card";

interface Draft {
  retention: number;
  attachments: number;
}

const toDraft = (p: PrivacyOut): Draft => ({
  retention: p.retention_days,
  attachments: p.attachment_retention_days,
});

/** Current value first if the server holds one outside the standard options. */
function withCurrent(options: readonly number[], current: number): number[] {
  return options.includes(current) ? [...options] : [...options, current].sort((a, b) => a - b);
}

function RetentionCard({ privacy }: { privacy: PrivacyOut }) {
  const { t } = useTranslation();
  const api = useApi();
  const wsId = useWorkspaceId();
  const message = useErrorMessage();
  const invalidate = useInvalidate();
  const manage = useCanManage("settings.manage");
  const [draft, setDraft] = useState(() => toDraft(privacy));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await unwrap(api.PUT("/v1/settings/privacy", { body: { retention_days: draft.retention, attachment_retention_days: draft.attachments, pii_redaction: kindsOf(privacy) } }));
      toast.success(t("settings.privacy.saved"));
      await invalidate(wsKeys.privacy(wsId));
    } catch (caught) {
      toast.error(message(caught));
    } finally {
      setBusy(false);
    }
  };
  const locked = !manage.allowed;
  return (
    <section className="card" aria-labelledby="retention-title">
      <div className="card-header">
        <h2 id="retention-title" className="card-title">
          {t("settings.privacy.retentionTitle")}
        </h2>
      </div>
      <fieldset className="divide-y divide-line" disabled={locked}>
        <div className="grid grid-cols-1 gap-4 px-5 py-4 md:grid-cols-2">
          <Field label={t("settings.privacy.keepConversations")} hint={privacy.effective_retention_note || t("settings.privacy.keepHint")}>
            <Select value={String(draft.retention)} onChange={(event) => { setDraft((d) => ({ ...d, retention: Number(event.target.value) })); }}>
              {withCurrent(RETENTION_DAY_OPTIONS, draft.retention).map((days) => (
                <option key={days} value={days}>
                  {t("settings.privacy.days", { count: days })}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t("settings.privacy.keepAttachments")}>
            <Select value={String(draft.attachments)} onChange={(event) => { setDraft((d) => ({ ...d, attachments: Number(event.target.value) })); }}>
              {withCurrent(ATTACHMENT_RETENTION_DAY_OPTIONS, draft.attachments).map((days) => (
                <option key={days} value={days}>
                  {t("settings.privacy.days", { count: days })}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </fieldset>
      <CardActions>
        <Button disabled={locked} loading={busy} title={manage.reason} onClick={() => void save()}>
          {t("settings.save")}
        </Button>
      </CardActions>
    </section>
  );
}

function ComplianceCard({ privacy }: { privacy: PrivacyOut }) {
  const { t } = useTranslation();
  const docs = [
    { href: privacy.dpa_url, label: t("settings.privacy.dpa") },
    { href: privacy.subprocessors_url, label: t("settings.privacy.subprocessors") },
  ];
  return (
    <section className="card card-body" aria-labelledby="compliance-title">
      <h2 id="compliance-title" className="card-title">
        {t("settings.privacy.complianceTitle")}
      </h2>
      <p className="mt-1 text-xs text-fg-muted">{t("settings.privacy.complianceBody")}</p>
      <div className="mt-3 space-y-2">
        {docs.map((doc) => (
          <a key={doc.href} href={doc.href} target="_blank" rel="noreferrer" className="flex w-full items-center gap-3 rounded-md border border-line px-3 py-2.5 text-left text-sm hover:border-primary">
            <Icon name="file-text" className="size-4 text-primary-ink" />
            <span className="flex-1 font-semibold">{doc.label}</span>
            <Icon name="download" className="size-4 text-fg-subtle" />
          </a>
        ))}
      </div>
      <div className="mt-3 flex items-start gap-2 rounded-md bg-surface-2 p-3 text-xs text-fg-muted">
        <Icon name="info" className="mt-0.5 size-4 shrink-0 text-primary-ink" />
        <span>{t("settings.privacy.npcNote")}</span>
      </div>
    </section>
  );
}

/** Data & privacy panel: no-training guarantee, redaction before AI, retention, DPA, export, delete. */
export function PrivacyPanel() {
  const { t } = useTranslation();
  const privacy = usePrivacySettings();
  if (privacy.isPending) return <CardSkeleton rows={4} />;
  if (privacy.isError) return <PanelError error={privacy.error} onRetry={() => void privacy.refetch()} />;
  return (
    <>
      <div className="flex items-start gap-3 rounded-lg border border-line bg-success-soft px-4 py-3">
        <Icon name="shield-check" className="mt-0.5 size-5 shrink-0 text-success" />
        <div className="text-sm">
          <div className="font-bold">{t("settings.privacy.noTrainingTitle")}</div>
          <p className="mt-0.5 text-fg-muted">{t("settings.privacy.noTrainingBody")}</p>
        </div>
      </div>
      <RedactionCard key={`redaction-${privacy.dataUpdatedAt}`} privacy={privacy.data} />
      <IdentityBindingCard />
      <EgressIpsCard />
      <WebhookReferenceCard />
      <RetentionCard key={privacy.dataUpdatedAt} privacy={privacy.data} />
      <RetentionPresetsCard />
      <SecurityActivityCard />
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <ComplianceCard privacy={privacy.data} />
        <DataExportCard />
      </div>
      <DangerZone />
    </>
  );
}
